import {createSelector} from 'reselect'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import apiverson from '../../../common/apiverson.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {buildPath} from '../../../common/querystring.js'
import {
  ensureStocktake,
  setStocktake,
  stocktakeSelector,
} from '../../../data/stocktakes.js'
import ButtonPrimary from '../../../common/components/Button/ButtonPrimary.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {
  HK_CONFIRM,
  HK_PACK_SHIP_BACK,
  HK_PACK_SHIP_NEXT,
} from '../../../common/constants/HotKeys.js'
import NumberInput from '../../../common/components/Form/NumberInput.js'
import {
  isNonZeroPositiveNumeric,
  isNumeric,
  isPositiveNumeric,
} from '../../../common/utils.js'
import ProductFilter from '../../../common/components/ProductFilter.js'
import {Count, Plural, PluralBlock} from '../../../common/components/Plural.js'
import BarcodeScannerListener from '../../../common/components/BarcodeScannerListener.js'
import {useEffect, useMemo} from 'react'
import {getRealDate} from '../../../common/date.js'
import PopoverImage from '../../../common/components/PopoverImage.js'
import {showImgsProductDetailSelector} from '../../../data/company.js'
import {
  IS_LINE_COUNTED_FILTER,
  IS_LINE_RECONCILED_FILTER,
  STOCKTAKE_LINE_COUNTED,
} from '../../../common/constants/Stocktakes.js'
import {
  stocktakeNavigateOnCountSaveSelector,
  showStocktakeSettingsModal,
  stocktakeMultiplierSelector,
  stocktakeShowMultiplierOnCountScreenSelector,
  STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT,
  STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT,
  stocktakeOnNewProductScanSelector,
  stocktakeAutosaveCountsSelector,
  STOCKTAKE__MULTIPLIER,
  stocktakeAutocountOnScanForKitsSelector,
} from './StocktakeSettingsModal.js'
import {currentPageSelector} from '../../../redux/selectors/ui/index.js'
import {STOCKTAKE_LIST_PAGE} from '../../../common/constants/Pages.js'
import {refreshStocktakeList} from '../stocktakeListFunctions.js'
import {refreshStocktakeDetails} from '../../StocktakeDetailPage/stocktakeDetailFunctions.js'
import Quantity from '../../../common/components/Quantity.js'
import api from '../../../common/api.js'
import TextArea from '../../../common/components/TextArea.js'
import {showConfirmMessageModal} from '../../../common/components/Modal/ConfirmMessageModal.js'
import className from '../../../common/className.js'
import {
  debouncedSaveUserSettings,
  hasProductUpdateInventoryPermissionSelector,
} from '../../../data/me.js'
import useToggleTimer from '../../../common/hooks/useToggleTimer.js'
import omit from 'lodash/omit.js'
import {getIsBOM, getIsKit} from '../../../data/products.js'
import Icon from '../../../common/icons/Icon.js'

const MODAL_FORM = 'stocktake_modal'
export const START_SUMMARY = -1
export const ADD_SKU = -2
export const SKU_LOADING = -3
export const SCAN_WARNING__NOT_FOUND = 'not_found'
export const SCAN_WARNING__NOT_ON_STOCKTAKE = 'not_on_stocktake'
export const SETTINGS = {
  AUTOSAVE_INTERVAL: 10 * 1000,
  INPUT_AUTOSAVE_LOCKOUT: 5 * 1000,
  BADGE_SHOW_TIME: 3 * 1000,
  WINDOW_SIZE: 100,
}

export async function showStocktakeModal({referenceID, lineQueryParams = {}}) {
  setForm(MODAL_FORM, {
    referenceID,
    panelID: START_SUMMARY,
    total: 0,
    totalCounted: 0,
    totalReconciled: 0,
    offset: -SETTINGS.WINDOW_SIZE,
    lineQueryParams: omit(lineQueryParams, ['offset', 'limit']),
    multiplier: stocktakeMultiplierSelector(getState()),
    counts: new Map(),
    isSavingCount: false,
    isSavingReconcile: false,
    isLoading: true,
    needsRefresh: false,
    serverError: null,
    scanWarning: null,
    lastInputDate: null,
    lastScannedCode: null,
    loadSKUSFromQueryParams: true,
    furthestIndexViewed: -1,
  })

  await getStocktakeLineTotal()

  await enforceLoadedWindow({force: true})

  const {total} = modalFormSelector(getState())

  if (total === 0) {
    await lockoutQueryParams()

    updateModalForm({panelID: ADD_SKU})
  }

  updateForm({isLoading: false})
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function updateCount(counted_quantity) {
  const {counts, panelID} = modalFormSelector(getState())

  const item = counts.get(panelID)

  item.counted_quantity = String(counted_quantity)

  updateModalForm({counts, lastInputDate: getRealDate()})
}

function updateComment(comment, type) {
  const {counts, panelID} = modalFormSelector(getState())

  const item = counts.get(panelID)

  if (type === 'count') {
    item.countComment = comment
  } else {
    item.reconcileComment = comment
  }

  updateModalForm({counts})
}

function updateMultiplier(multiplier) {
  updateModalForm({multiplier})

  multiplier = Number(multiplier)

  if (!isNaN(multiplier)) {
    debouncedSaveUserSettings({[STOCKTAKE__MULTIPLIER]: multiplier})
  }
}

export async function closeModal() {
  const {needsRefresh} = modalFormSelector(getState())

  removeForm(MODAL_FORM)

  if (needsRefresh) {
    if (currentPageSelector(getState()) === STOCKTAKE_LIST_PAGE) {
      await refreshStocktakeList()
    } else {
      await refreshStocktakeDetails()
    }
  }
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

function nextPanelIDSelector(state) {
  const {panelID, counts, total, loadSKUSFromQueryParams} =
    modalFormSelector(state)

  if (panelID === ADD_SKU) {
    // nothing after add sku
    return null
  }

  const skus = [...counts.keys()]
  const onLastLoadedSKU = panelID === skus[skus.length - 1]
  // we are on the last "added" sku IF:
  // this stocktake is empty
  // OR
  // we are on the last loaded SKU AND
  //   the total stocktake SKUS matches the loaded SKUs
  //   OR we aren't loading skus from query params
  const onLastAddedSKU =
    total === 0 ||
    (onLastLoadedSKU && (total === skus.length || !loadSKUSFromQueryParams))

  // just started
  if (panelID === START_SUMMARY) {
    // if we have skus loaded then first sku otherwise add sku
    return skus.length > 0 ? skus[0] : onLastAddedSKU ? ADD_SKU : SKU_LOADING
  }

  // on last line
  if (onLastLoadedSKU) {
    return onLastAddedSKU ? ADD_SKU : SKU_LOADING
  }

  const indexOfSKU = skus.indexOf(panelID)

  if (indexOfSKU + 1 < skus.length) {
    return skus[indexOfSKU + 1]
  }

  return null
}

function backPanelIDSelector(state) {
  const {panelID, counts} = modalFormSelector(state)

  if (panelID === START_SUMMARY) {
    // nothing before start
    return null
  }

  const skus = [...counts.keys()]

  if (panelID === ADD_SKU) {
    // if we have skus loaded then last sku otherwise no back panel
    return skus.length > 0 ? skus[skus.length - 1] : null
  }

  const indexOfSKU = skus.indexOf(panelID)

  // on first line
  if (indexOfSKU === 0) {
    return START_SUMMARY
  }

  if (indexOfSKU - 1 >= 0) {
    return skus[indexOfSKU - 1]
  }

  return null
}

function summaryOfChangesSelector(state) {
  const {counts} = modalFormSelector(state)

  const unsavedCounts = []

  for (const [sku, item] of counts.entries()) {
    const alreadySavedBefore = item.line.counted_quantity !== null
    const isClearedCount = item.counted_quantity === ''
    const isValid = isPositiveNumeric(item.counted_quantity) || isClearedCount

    const hasChangedFromSaved = alreadySavedBefore
      ? Number(item.counted_quantity) !== item.line.counted_quantity
      : !isClearedCount

    // if we have saved this count previously then blank string is unsetting it
    const isUncounted = alreadySavedBefore && isClearedCount

    if (hasChangedFromSaved) {
      unsavedCounts.push({sku, alreadySavedBefore, isUncounted, isValid, item})
    }
  }

  return unsavedCounts
}

function validChangesSelector(state) {
  const summaryOfChanges = summaryOfChangesSelector(state)

  return summaryOfChanges.filter(({isValid}) => isValid)
}

function lineCountsSelector(state) {
  const {referenceID} = modalFormSelector(state)
  const stocktake = stocktakeSelector(state, {referenceID})

  return stocktake.line_count
}

export const errorsSelector = createSelector(
  modalFormSelector,
  summaryOfChangesSelector,
  (form, summaryOfChanges) => {
    const errors = {}

    if (!isNonZeroPositiveNumeric(form.multiplier)) {
      errors.multiplier = 'Positive numbers only'
    }

    for (const {sku, isValid} of summaryOfChanges) {
      if (!isValid) {
        errors[sku] = `Value must be a positive number or blank`
      }
    }

    return errors
  },
)

async function getStocktakeLineTotal() {
  let {
    referenceID,
    lineQueryParams,
    loadSKUSFromQueryParams,
    total,
    totalCounted,
    totalReconciled,
  } = modalFormSelector(getState())

  if (loadSKUSFromQueryParams) {
    const [
      {
        json: {count: total_count},
      },
      {
        json: {count: total_counted},
      },
      {
        json: {count: total_reconciled},
      },
    ] = await Promise.all([
      apiverson.get(
        buildPath(['stocktake', referenceID, 'line'], {
          ...lineQueryParams,
          limit: 0,
        }),
      ),
      apiverson.get(
        buildPath(['stocktake', referenceID, 'line'], {
          ...lineQueryParams,
          [IS_LINE_COUNTED_FILTER]: true,
          limit: 0,
        }),
      ),
      apiverson.get(
        buildPath(['stocktake', referenceID, 'line'], {
          ...lineQueryParams,
          [IS_LINE_RECONCILED_FILTER]: true,
          limit: 0,
        }),
      ),
    ])

    total = total_count
    totalCounted = total_counted
    totalReconciled = total_reconciled
  } else {
    await ensureStocktake(referenceID, {reload: true})

    const stocktake = stocktakeSelector(getState(), {referenceID})

    total = stocktake.line_count.total
    totalCounted = stocktake.line_count.counted
    totalReconciled = stocktake.line_count.reconciled
  }

  updateModalForm({
    total,
    totalCounted,
    totalReconciled,
  })
}

export async function handleStocktakeScan(
  scannedCode,
  {forceAdd = false, incrementCount = true} = {},
) {
  try {
    // we got a code. It should be a SKU or maybe UPC
    let sku = null

    updateModalForm({
      serverError: null,
      scanWarning: null,
      lastScannedCode: scannedCode,
    })

    let {counts, referenceID, multiplier} = modalFormSelector(getState())

    const stocktakeOnNewProductScan =
      stocktakeOnNewProductScanSelector(getState())
    const autocountOnScanForKits =
      stocktakeAutocountOnScanForKitsSelector(getState())

    multiplier = Number(multiplier) || 1

    const [, item] =
      [...counts.entries()].find(
        ([, item]) =>
          item.line.product.sku === scannedCode ||
          item.line.product.upc === scannedCode,
      ) || []

    if (item) {
      // scanned already loaded sku
      sku = item.line.product.sku
      const isKit = getIsKit(item.line.product)
      incrementCount = isKit && !autocountOnScanForKits ? false : incrementCount

      if (incrementCount) {
        item.counted_quantity = item.counted_quantity
          ? Number(item.counted_quantity) + multiplier
          : multiplier
      }

      updateModalForm({counts, panelID: sku})

      return
    }

    // scanned something that doesn't match our loaded SKUs
    updateModalForm({isLoading: true})

    // try to find the thing on the stocktake
    const {json} = await apiverson.get(
      buildPath(['stocktake', referenceID, 'line'], {barcode: scannedCode}),
    )

    let lines = json.stocktake_line
    const productOnStocktake = lines.length > 0
    let addedProduct = false
    let scanWarning = null

    if (productOnStocktake) {
      // lockout loading skus from the query params since the user doesn't care about that order
      await lockoutQueryParams()

      sku = lines[0].product.sku
    } else {
      if (
        [
          STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_DO_NOT_COUNT,
          STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT,
        ].includes(stocktakeOnNewProductScan) ||
        forceAdd
      ) {
        addedProduct = true

        const {
          json: {
            product: [product],
          },
        } = await api.get('/product', {barcode: scannedCode})

        if (product) {
          // lockout now that we have added the product
          await lockoutQueryParams()

          sku = product.sku

          await addProduct(sku)

          const res = await apiverson.get(
            buildPath(['stocktake', referenceID, 'line'], {
              sku,
            }),
          )

          lines = res.json.stocktake_line

          await getStocktakeLineTotal()
        } else {
          scanWarning = SCAN_WARNING__NOT_FOUND
          addedProduct = false
        }
      } else {
        scanWarning = SCAN_WARNING__NOT_ON_STOCKTAKE
      }
    }

    if (lines && (productOnStocktake || addedProduct)) {
      for (const line of lines) {
        addToCountMap(line)
        const isKit = getIsKit(line.product)
        incrementCount =
          isKit && !autocountOnScanForKits ? false : incrementCount

        if (
          incrementCount &&
          (productOnStocktake ||
            stocktakeOnNewProductScan ===
              STOCKTAKE__ON_NEW_PRODUCT_SCAN__ADD_AND_COUNT)
        ) {
          const item = counts.get(line.product.sku)

          item.counted_quantity = item.counted_quantity
            ? Number(item.counted_quantity) + multiplier
            : multiplier
        }
      }
    }

    if (scanWarning) {
      updateModalForm({
        isLoading: false,
        scanWarning,
      })
    } else {
      updateModalForm({
        isLoading: false,
        panelID: sku,
        ...(addedProduct ? {needsRefresh: true} : null),
      })
    }
  } catch (err) {
    updateModalForm({
      serverError: `Error scanning for SKU: ${
        err.message || err.error_message
      }`,
      isLoading: false,
    })
  }
}

async function lockoutQueryParams() {
  const {counts, furthestIndexViewed, loadSKUSFromQueryParams} =
    modalFormSelector(getState())

  if (!loadSKUSFromQueryParams) {
    return
  }

  // clear out all SKUs that haven't been looked at
  const entries = [...counts.entries()]

  counts.clear()

  for (const [index, [sku, item]] of entries.entries()) {
    if (index > furthestIndexViewed) {
      break
    }

    counts.set(sku, item)
  }

  updateModalForm({loadSKUSFromQueryParams: false, counts})

  await getStocktakeLineTotal()
}

export async function enforceLoadedWindow({force = false} = {}) {
  try {
    let {
      total,
      counts,
      referenceID,
      offset,
      lineQueryParams,
      panelID,
      isLoading,
      loadSKUSFromQueryParams,
      furthestIndexViewed,
    } = modalFormSelector(getState())
    const windowSize = SETTINGS.WINDOW_SIZE

    // query params are no longer valid
    if (!loadSKUSFromQueryParams) {
      return
    }

    let index = -1
    if (typeof panelID === 'string') {
      const skus = [...counts.keys()]
      index = skus.indexOf(panelID)

      // enforceLoadedWindow is called for every panel change
      // so burn in the furthest index so we know what to clear
      // if we lockout the query params
      if (furthestIndexViewed < index) {
        updateModalForm({furthestIndexViewed: index})
      }
    }

    if (isLoading && !force) {
      return
    }

    const canLoadMore = force || total > offset + windowSize

    if (!canLoadMore) {
      return
    }

    if (index > -1) {
      const pageNumber = Math.floor(index / windowSize)
      const pageCount = Math.floor(offset / windowSize)
      const pageIndex = index % windowSize

      // if we still have plenty of SKUs to work through
      // then don't try to load more
      if (!(pageNumber === pageCount && pageIndex >= windowSize * 0.8)) {
        return
      }
    }

    // now we are actually going to load more SKUs
    updateModalForm({isLoading: true, serverError: null})

    offset += windowSize

    const {json} = await apiverson.get(
      buildPath(['stocktake', referenceID, 'line'], {
        ...lineQueryParams,
        limit: windowSize,
        offset,
      }),
    )

    for (const line of json.stocktake_line) {
      addToCountMap(line)
    }

    updateModalForm({
      counts,
      isLoading: false,
      offset: json.offset,
    })
  } catch (err) {
    updateModalForm({
      serverError: `Error loading SKUs from stocktake: ${
        err.message || err.error_message
      }`,
      isLoading: false,
    })
  }
}

export function buildCountItem(line, warehouseID) {
  const warehouse = line.product.warehouses.find(({id}) => id === warehouseID)

  return {
    counted_quantity:
      line.counted_quantity === null ? '' : String(line.counted_quantity),
    line,
    warehouse,
    countComment: '',
    reconcileComment: '',
    get needSave() {
      return this.line.counted_quantity === null
        ? this.counted_quantity !== ''
        : this.line.counted_quantity !== Number(this.counted_quantity)
    },
  }
}

function addToCountMap(line) {
  // this function does not trigger a state change
  // so be sure to call updateModalForm() after calling it

  const {referenceID, counts} = modalFormSelector(getState())
  const stocktake = stocktakeSelector(getState(), {referenceID})

  counts.set(line.product.sku, buildCountItem(line, stocktake.warehouse_id))
}

export async function onNewProductSearchAdd(product) {
  try {
    updateModalForm({isLoading: true, serverError: null})

    const {referenceID} = modalFormSelector(getState())

    await addProduct(product.sku)

    const {json} = await apiverson.get(
      buildPath(['stocktake', referenceID, 'line'], {
        sku: product.sku,
      }),
    )

    await lockoutQueryParams()

    for (const line of json.stocktake_line) {
      addToCountMap(line)
    }

    await getStocktakeLineTotal()

    updateModalForm({
      isLoading: false,
      panelID: product.sku,
      needsRefresh: true,
    })
  } catch (err) {
    updateModalForm({
      serverError: `Error adding product to stocktake: ${
        err.message || err.error_message
      }`,
      isLoading: false,
    })
  }
}

async function addProduct(sku) {
  const {referenceID} = modalFormSelector(getState())

  const {json: stocktake} = await apiverson.post(
    buildPath(['stocktake', referenceID, 'line']),
    {
      criteria: {sku: [sku]},
    },
  )

  setStocktake(stocktake)
}

export async function commitStocktake({forced, sku, onSuccess} = {}) {
  try {
    let {referenceID, lastInputDate} = modalFormSelector(getState())
    const validChanges = validChangesSelector(getState()).filter(
      (i) => !sku || i.sku === sku,
    )

    const now = getRealDate()

    const inputJustHappened = lastInputDate
      ? now - lastInputDate < SETTINGS.INPUT_AUTOSAVE_LOCKOUT
      : false

    if ((!forced && inputJustHappened) || validChanges.length === 0) {
      return
    }

    const stocktake = stocktakeSelector(getState(), {referenceID})

    updateModalForm({isSavingCount: true, serverError: null})

    if (!stocktake.started_date) {
      await apiverson.put(buildPath(['stocktake', referenceID]), {
        started_date: now.toISOString(),
      })
    }

    await Promise.all(
      validChanges.map(async ({item}) => {
        if (item.line.expected_quantity === null) {
          await apiverson.post(
            buildPath([
              'stocktake',
              referenceID,
              'line',
              item.line.id,
              'start',
            ]),
            {},
          )
        }

        const payload = {
          counted_quantity: Number(item.counted_quantity),
        }

        if (item.countComment) {
          payload.comment = item.countComment
        }

        const {json: line} = await apiverson.post(
          buildPath(['stocktake', referenceID, 'line', item.line.id, 'count']),
          payload,
        )

        item.line = line
      }),
    )

    updateModalForm({needsRefresh: true})

    if (forced && stocktakeNavigateOnCountSaveSelector(getState())) {
      const nextPanelID = nextPanelIDSelector(getState())

      updateModalForm({panelID: nextPanelID})
    }

    await ensureStocktake(referenceID, {reload: true})

    await getStocktakeLineTotal()

    if (onSuccess) {
      onSuccess()
    } else {
      showMessageToast('Recorded stocktake')
    }

    updateModalForm({isSavingCount: false})
  } catch (err) {
    updateModalForm({
      serverError: `Error committing stocktake: ${
        err.message || err.error_message
      }`,
      isSavingCount: false,
    })
  }
}

async function reconcileStocktake(sku) {
  try {
    if (!hasProductUpdateInventoryPermissionSelector(getState())) {
      return
    }

    let {referenceID, counts} = modalFormSelector(getState())

    updateModalForm({isSavingReconcile: true, serverError: null})

    const item = counts.get(sku)

    if (item.line.counted_quantity === null) {
      return
    }

    const {json: line} = await apiverson.post(
      buildPath(['stocktake', referenceID, 'line', item.line.id, 'reconcile']),
      {
        comment: item.reconcileComment || undefined,
      },
    )

    item.line = line

    updateModalForm({counts, needsRefresh: true})

    await ensureStocktake(referenceID, {reload: true})

    await getStocktakeLineTotal()

    showMessageToast('Reconciled stocktake')

    updateModalForm({isSavingReconcile: false})
  } catch (err) {
    updateModalForm({
      serverError: `Error reconciling stocktake: ${
        err.message || err.error_message
      }`,
      isSavingReconcile: false,
    })
  }
}

function useAutoSave(enable, onSave, timeout = 30 * 1000) {
  useEffect(() => {
    if (!enable) {
      return
    }

    let timeoutRef = null

    async function doIt() {
      await onSave()

      timeoutRef = setTimeout(doIt, timeout)
    }

    timeoutRef = setTimeout(doIt, timeout)

    return () => {
      clearTimeout(timeoutRef)
    }
  }, [enable])
}

async function showCommentModal(value, type) {
  const results = await showConfirmMessageModal({
    title: `Edit ${type === 'count' ? 'Count' : 'Reconcile'} Explanation`,
    confirmText: 'Save',
    cancelText: 'Cancel',
    className: 'modal--fit-screen-sm',
    Message: ({form, updateModalForm}) => (
      <ul className="list list--no-style">
        <li>
          <TextArea
            label="Explanation"
            rows="10"
            id="comment"
            value={form.confirmValue}
            onChange={(value) => updateModalForm({confirmValue: value})}
            autoFocus
          />
        </li>
      </ul>
    ),
    confirmValue: value,
  })

  if (results === false) {
    return
  }

  updateComment(results, type)
}

function LineSummary() {
  const {totalCounted, totalReconciled} = useSelector(modalFormSelector)

  return (
    <PluralBlock count={totalCounted}>
      <div className="fs-00 lh-md">
        <span>
          <Count /> <Plural word="Count" /> Saved
        </span>
        <span className="margin-left-7 margin-right-7">·</span>
        <span>
          <Quantity value={totalReconciled} /> Reconciled
        </span>
      </div>
    </PluralBlock>
  )
}

function StocktakeSummary() {
  const {total} = useSelector(modalFormSelector)

  return (
    <PluralBlock count={total}>
      <div className="fs-02 lh-md">
        <strong>
          <Count /> <Plural word="SKU" /> in this Stocktake
        </strong>
      </div>
    </PluralBlock>
  )
}

function LinePanel() {
  const form = useSelector(modalFormSelector)
  const errors = useSelector(errorsSelector)
  const showImgsProductDetail = useSelector(showImgsProductDetailSelector)
  const item = form.counts.get(form.panelID)
  const validChanges = useSelector(validChangesSelector)
  const stocktakeShowMultiplierOnCountScreen = useSelector(
    stocktakeShowMultiplierOnCountScreenSelector,
  )
  const multiplier = useSelector(stocktakeMultiplierSelector)
  const hasProductUpdateInventoryPermission = useSelector(
    hasProductUpdateInventoryPermissionSelector,
  )

  useEffect(() => {
    updateModalForm({multiplier})
  }, [multiplier])

  if (!item) {
    return null
  }

  const {
    line: {product, ...line},
    warehouse,
  } = item

  const countedQuantity = isNumeric(item.counted_quantity)
    ? Number(item.counted_quantity)
    : null
  const expectedQuantity =
    line.expected_quantity === null
      ? warehouse.physical_on_hand
      : line.expected_quantity
  const difference =
    countedQuantity !== null ? countedQuantity - expectedQuantity : null

  const hasUnsavedChanges = !!validChanges.find(({sku}) => sku === product.sku)
  const isKit = getIsKit(product)
  const isBOM = getIsBOM(product)

  return (
    <div className="margin-top-30 margin-top-sm-15">
      <div className="wrap--st-product-info fs-00 lh-md flex--justify">
        <div className="wrap--st-product-name wrap--xl-img-popover flex w-70">
          {showImgsProductDetail && (
            <PopoverImage src={product.image_url} alt={product.name} />
          )}
          <div className="margin-left-10">
            <a
              className="btn--link darker"
              href={`#/product/${encodeURIComponent(product.sku)}`}
            >
              <strong className="fs-03 fs-sm-01 lh-sm-md block">
                {product.name}
              </strong>
              <div className="fs-01 fs-sm-n0 lh-md">
                <strong>SKU:</strong> {product.sku}
              </div>
              {warehouse.location_in_warehouse && (
                <div className="fs-01 fs-sm-n0 lh-md">
                  <strong className="margin-right-5">Location:</strong>{' '}
                  {warehouse.location_in_warehouse}
                </div>
              )}
            </a>
            {(isKit || isBOM) && (
              <div className="margin-top-7">
                <span className="label__callout label__callout--lg label__callout--grey inline-block">
                  <strong>{isKit ? 'KIT' : 'BOM'}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
        {stocktakeShowMultiplierOnCountScreen && (
          <div className="wrap--st-multiplier">
            <ul className="alert alert--neutral list list--no-style flex flex--jc-end">
              <li
                className="list__item margin-bottom-0 tooltip--left"
                data-hint="Set the multiplier for a single barcode scan. Default is 1."
              >
                <Icon
                  name="scanner"
                  className="text--md-grey fs-02 lh-sm v-align-middle margin-right-7"
                />
                <span className="text--lt-grey fs-01 lh-sm v-align-middle margin-right-10">
                  x
                </span>
              </li>
              <li className="list__item margin-bottom-0">
                <NumberInput
                  id="multiplier"
                  value={form.multiplier}
                  onChange={(multiplier) => updateMultiplier(multiplier)}
                  errorMessage={errors.multiplier}
                />
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="wrap--st-count-section flex margin-top-30 margin-top-sm-10 margin-bottom-sm-10 margin-bottom-30">
        <div className="outer-wrap--st-count-input w-50 margin-right-40">
          <div className="flex">
            <div className="divider--right padding-right-35 margin-right-35">
              <dl className="list">
                <dt className="list__title fs-01 lh-md margin-bottom-3">
                  Expected Qty
                </dt>
                <dd className="list__item fs- margin-bottom-0">
                  <strong className="fs-05 lh-sm word-break">
                    <Quantity value={expectedQuantity} />
                  </strong>
                </dd>
              </dl>
            </div>
            <div>
              <NumberInput
                id="counted_quantity"
                label="Counted Qty"
                key={line.id}
                value={item.counted_quantity}
                onChange={(value) => updateCount(value)}
                errorMessage={errors[product.sku]}
                size="lg"
              />
            </div>
          </div>
          {difference > 0 ? (
            <div className="alert alert--warning alert--sm align-center margin-top-25">
              <span className="fs-00 lh-md">
                Count has <Quantity value={difference} /> more than expected.
              </span>
            </div>
          ) : difference < 0 ? (
            <div className="alert alert--warning alert--sm align-center margin-top-25">
              <span className="fs-00 lh-md">
                Count has <Quantity value={Math.abs(difference)} /> less than
                expected.
              </span>
            </div>
          ) : null}
          {line.reconciled_date !== null ? (
            <div className="alert alert--success-no-bg alert--sm align-center margin-top-25">
              <span className="fs-00 lh-md">Count has been reconciled.</span>
            </div>
          ) : line.counted_quantity !== null ? (
            <div className="alert alert--standard alert--sm align-center margin-top-25">
              <span className="fs-00 lh-md">Count has been saved.</span>
            </div>
          ) : null}
        </div>
        <div className="outer-wrap--count-save w-40 flex-grow">
          <div className="wrap--st-count-save alert alert--standard alert--lg margin-bottom-10">
            <div
              className={className`flex ${{'align-items-center': !item.countComment}} ${hasProductUpdateInventoryPermission && 'divider--bottom padding-bottom-20 margin-bottom-20'}`}
            >
              <div className="w-40 margin-right-20">
                <ButtonPrimary
                  className="break-spaces w-100"
                  onClick={() =>
                    commitStocktake({forced: true, sku: product.sku})
                  }
                  isDisabled={errors.preventSave || !hasUnsavedChanges}
                  isLoading={form.isSavingCount}
                >
                  <span className="lh-sm">Save Count</span>
                </ButtonPrimary>
              </div>
              <div className="wrap--st-count-explanation w-55">
                {item.countComment && (
                  <p className="fs-n0 lh-sm-md margin-bottom-3">
                    <em>{item.countComment}</em>
                  </p>
                )}
                <ButtonLink
                  className={className`fs-n0 ${item.countComment ? 'lh-sm-md' : 'lh-md'} border-underline`}
                  onClick={() => showCommentModal(item.countComment, 'count')}
                >
                  {item.countComment ? 'Edit' : 'Add an explanation'}
                </ButtonLink>
              </div>
            </div>
            {hasProductUpdateInventoryPermission && (
              <div
                className={className`flex ${{'align-items-center': !item.reconcileComment}}`}
              >
                <div className="w-40 margin-right-20">
                  <ButtonPrimary
                    alt
                    className="break-spaces w-100"
                    onClick={() => reconcileStocktake(product.sku)}
                    isDisabled={
                      line.counted_quantity === null || hasUnsavedChanges
                    }
                    isLoading={form.isSavingReconcile}
                  >
                    <span className="lh-sm">Reconcile</span>
                  </ButtonPrimary>
                </div>
                <div className="wrap--st-count-explanation w-55">
                  {item.reconcileComment && (
                    <p className="fs-n0 lh-sm-md margin-bottom-3">
                      <em>{item.reconcileComment}</em>
                    </p>
                  )}
                  <ButtonLink
                    className={className`fs-n0 ${item.reconcileComment ? 'lh-sm-md' : 'lh-md'} border-underline`}
                    onClick={() =>
                      showCommentModal(item.reconcileComment, 'reconciled')
                    }
                  >
                    {item.reconcileComment ? 'Edit' : 'Add an explanation'}
                  </ButtonLink>
                </div>
              </div>
            )}
          </div>
          <p className="fs-n0 lh-md op-75">
            <em>
              Saving the count will NOT update actual inventory levels for the
              SKU. You must reconcile for that to occur.
            </em>
          </p>
        </div>
      </div>
    </div>
  )
}

function StartSummaryPanel() {
  const {isLoading, referenceID} = useSelector(modalFormSelector)
  const nextPanelID = useSelector(nextPanelIDSelector)
  const lineCounts = useSelector(lineCountsSelector)

  return (
    <div>
      <div className="divider--bottom">
        <StocktakeSummary />
        <LineSummary />
      </div>
      <div className="row flex flex--reset-sm margin-bottom-sm-10 margin-top-30 margin-top-sm-10 padding-top-10 margin-bottom-30">
        <div className="medium-7 columns divider--right padding-right-50 margin-right-30 padding-bottom-30 outer-wrap--product-search-100 wrap--label-lg border-reset padding-right-reset margin-bottom-reset">
          <p className="fs-02 lh-md margin-bottom-20 margin-bottom-sm-10">
            <strong>Go to a Specific SKU</strong>
          </p>
          <div className="flex">
            <div className="margin-right-25 margin-right-reset">
              <ProductFilter
                label="Enter a SKU or Scan a Barcode"
                placeholder="Search SKUs already in stocktake..."
                dropdown="STOCKTAKE_MODAL_PRODUCT_FILTER"
                queryParams={{stocktake_reference_id: referenceID}}
                onSelect={(product) =>
                  handleStocktakeScan(product.sku, {incrementCount: false})
                }
              />
            </div>
            <div className="flex align-items-center hide-for-sm">
              <Icon
                name="scanner"
                className="fs-06 lh-sm margin-right-3 flip-h op-50"
              />
              <Icon name="barcode-03" className="fs-07 lh-sm op-50" />
            </div>
          </div>
        </div>
        <div className="medium-5 columns padding-bottom-30">
          <p className="fs-02 lh-md margin-bottom-25 margin-bottom-sm-10">
            <strong>
              Dive{lineCounts[STOCKTAKE_LINE_COUNTED] > 0 && ' Back'} In
            </strong>
          </p>
          <div className="flex">
            <ButtonPrimary
              size="x-md"
              className="wider flex align-items-center"
              isDisabled={nextPanelID === null || isLoading}
              onClick={() => updateModalForm({panelID: nextPanelID})}
              hotKey={HK_PACK_SHIP_NEXT}
            >
              <span className="margin-right-5">
                {lineCounts[STOCKTAKE_LINE_COUNTED] === 0
                  ? 'Start Stocktake'
                  : 'Continue Stocktake'}
              </span>
              <Icon name="arrow-right" className="fs-01 lh-0" />
            </ButtonPrimary>
          </div>
        </div>
      </div>
      <div className="row margin-bottom-30">
        <div className="medium-5 columns medium-centered">
          <ButtonPrimary
            isOutlined
            size="md"
            className="w-100 btn--reset-sm"
            isDisabled={nextPanelID === null || isLoading}
            onClick={() => updateModalForm({panelID: ADD_SKU})}
            hotKey={HK_PACK_SHIP_NEXT}
          >
            + Add a SKU to this Stocktake
          </ButtonPrimary>
        </div>
      </div>
    </div>
  )
}

function AddSKUPanel() {
  const {isLoading, total, counts} = useSelector(modalFormSelector)
  const backPanelID = useSelector(backPanelIDSelector)

  const scanningOutOfOrder = total > counts.size

  return (
    <div className="margin-bottom-30">
      <div className="flex align-items-center divider--bottom">
        {backPanelID && (
          <ButtonLink
            className="flex align-items-center margin-right-10 no-underline"
            disabled={isLoading}
            onClick={() => updateModalForm({panelID: backPanelID})}
            hotKey={HK_PACK_SHIP_BACK}
          >
            <Icon name="arrow-left" className="fs-02 lh-sm" />
          </ButtonLink>
        )}
        <div>
          <StocktakeSummary />
          <LineSummary />
        </div>
      </div>
      <p className="fs-03 lh-ms-md margin-top-25 margin-top-sm-15 margin-bottom-30 margin-bottom-sm-15">
        {scanningOutOfOrder ? (
          <strong>Add or look up a SKU for this stocktake.</strong>
        ) : (
          <strong>Add a SKU to this stocktake.</strong>
        )}
      </p>
      <div className="wrap--st-add-sku-form row flex padding-bottom-20">
        <div className="medium-6 small-12 columns divider--right padding-right-50 margin-right-30 outer-wrap--product-search-100 wrap--label-lg border-reset padding-right-sm-10 margin-right-reset">
          <ProductFilter
            label={scanningOutOfOrder ? 'Find a SKU' : 'Find a SKU to Add'}
            placeholder="Search your products"
            dropdown="STOCKTAKE_MODAL_PRODUCT_FILTER"
            onSelect={onNewProductSearchAdd}
          />
        </div>
        <div className="medium-6 small-12 columns">
          <p className="fs-01 lh-md margin-bottom-25 margin-top-sm-25 margin-bottom-reset">
            <strong>Or Scan a Barcode on a Product</strong>
          </p>
          <div className="flex align-items-center">
            <Icon
              name="scanner"
              className="fs-07 lh-sm margin-right-3 flip-h op-50"
            />
            <Icon name="barcode-03" className="fs-08 lh-sm op-50" />
          </div>
        </div>
      </div>
    </div>
  )
}

function NextBackCounter() {
  const {referenceID, isLoading, panelID, counts, total} =
    useSelector(modalFormSelector)
  const backPanelID = useSelector(backPanelIDSelector)
  const nextPanelID = useSelector(nextPanelIDSelector)
  const position = useMemo(() => {
    const skus = [...counts.keys()]
    const index = skus.indexOf(panelID)

    return index !== -1 ? index + 1 : null
  }, [panelID])

  return (
    <ul className="list list--no-style flex--justify align-items-center divider--bottom">
      <li className="list__item margin-bottom-0">
        <PluralBlock count={total}>
          <div className="fs-02 lh-md">
            <strong>
              {position ? `${position} of ` : ''}
              <Count /> <Plural word="SKU" />
            </strong>
          </div>
        </PluralBlock>
        <LineSummary />
      </li>
      <li className="list__item flex align-items-center margin-top-sm-10 margin-bottom-0">
        <div className="outer-wrap--product-search-stocktake outer-wrap--product-search-100 margin-right-15 margin-right-sm-7">
          <ProductFilter
            placeholder="Find a product in this stocktake..."
            dropdown="STOCKTAKE_MODAL_PRODUCT_FILTER"
            onSelect={(product) =>
              handleStocktakeScan(product.sku, {incrementCount: false})
            }
            queryParams={{stocktake_reference_id: referenceID}}
          />
        </div>
        <div>
          <ButtonLink
            className="no-underline"
            title="Add a SKU to this stocktake"
            onClick={() => updateModalForm({panelID: ADD_SKU})}
          >
            <span className="fs-04 lh-sm">+</span>
          </ButtonLink>
        </div>
      </li>
      <li className="list__item flex align-items-center margin-top-sm-10 margin-bottom-0">
        <ButtonLink
          className="flex align-items-center margin-right-20 margin-right-sm-10 no-underline"
          disabled={backPanelID === null || isLoading}
          onClick={() => updateModalForm({panelID: backPanelID})}
          hotKey={HK_PACK_SHIP_BACK}
        >
          <Icon name="arrow-left" className="fs-03 lh-sm" />
        </ButtonLink>
        <ButtonLink
          className="flex align-items-center no-underline"
          disabled={nextPanelID === null || isLoading}
          onClick={() => updateModalForm({panelID: nextPanelID})}
          hotKey={HK_PACK_SHIP_NEXT}
        >
          <Icon name="arrow-right" className="fs-03 lh-sm" />
        </ButtonLink>
      </li>
    </ul>
  )
}

function ScanWarning() {
  const {scanWarning, lastScannedCode} = useSelector(modalFormSelector)

  return (
    <div className="align-center">
      <div className="inline-block margin-top-30 margin-bottom-30 alert alert--warning alert--lg margin-auto align-center">
        {scanWarning === SCAN_WARNING__NOT_ON_STOCKTAKE ? (
          <p className="fs-02 lh-md margin-bottom-15">
            <strong>This SKU is NOT on the stocktake.</strong>
          </p>
        ) : scanWarning === SCAN_WARNING__NOT_FOUND ? (
          <p className="fs-02 lh-md margin-bottom-15">
            <strong>The SKU could not be found in your account.</strong>
          </p>
        ) : null}
        <div>
          <ButtonPrimary
            isOutlined
            size="x-md"
            onClick={() => updateModalForm({scanWarning: null})}
            hotKey={HK_CONFIRM}
          >
            Go Back
          </ButtonPrimary>
          {scanWarning === SCAN_WARNING__NOT_ON_STOCKTAKE && (
            <ButtonPrimary
              alt
              size="x-md"
              className="margin-left-15"
              onClick={() => {
                handleStocktakeScan(lastScannedCode, {forceAdd: true})
              }}
            >
              Add SKU to Stocktake
            </ButtonPrimary>
          )}
        </div>
      </div>
    </div>
  )
}

function StocktakeModal({form}) {
  const errors = useSelector(errorsSelector)
  const unsavedCount = useSelector(validChangesSelector).length
  const stocktakeAutosaveCounts = useSelector(stocktakeAutosaveCountsSelector)
  const [showCountAutoSaved, startShowCountAutoSaved] = useToggleTimer(
    SETTINGS.BADGE_SHOW_TIME,
  )

  useEffect(() => {
    enforceLoadedWindow()
  }, [form.panelID])

  useAutoSave(
    stocktakeAutosaveCounts,
    () => commitStocktake({onSuccess: startShowCountAutoSaved}),
    SETTINGS.AUTOSAVE_INTERVAL,
  )

  return (
    <ConfirmModal
      title={`Stocktake ${form.referenceID}`}
      modalSize="lg"
      className="modal--stocktake modal--fit-screen-sm"
      onConfirm={() => commitStocktake({forced: true})}
      onCancel={() => closeModal()}
      confirmText={
        unsavedCount > 1
          ? `Save ${unsavedCount} Counts in Session`
          : 'Save Session'
      }
      cancelText="Close"
      RightButtons={() => (
        <ButtonLink
          className="no-underline"
          onClick={() => showStocktakeSettingsModal()}
        >
          <Icon name="cog" className="fs-03 lh-sm" />
        </ButtonLink>
      )}
      badgeAnimate={showCountAutoSaved}
      badgeContent={showCountAutoSaved ? 'Auto-Saved Counts' : null}
      isSaving={form.isSavingCount}
      isDisabled={errors.preventSave || unsavedCount === 0}
      error={form.serverError}
      preventHotKeyConfirm
    >
      {form.isLoading ? (
        <div className="loading align-center margin-top-30 margin-bottom-30">
          <span className="list-processing animate-spin v-align-middle" />
        </div>
      ) : (
        <>
          <BarcodeScannerListener onScan={handleStocktakeScan} capturePaste />
          {form.scanWarning ? (
            <ScanWarning />
          ) : form.panelID === START_SUMMARY ? (
            <StartSummaryPanel />
          ) : form.panelID === ADD_SKU ? (
            <AddSKUPanel />
          ) : form.panelID === SKU_LOADING ? (
            <div className="loading align-center">
              <span className="list-processing animate-spin v-align-middle" />
            </div>
          ) : (
            <>
              <NextBackCounter />
              <LinePanel />
            </>
          )}
        </>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(StocktakeModal, modalFormSelector)
